import React, { useRef, useState } from "react";
import { Upload, ImageIcon, ImageOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { resolveImageUrl } from "@/lib/site";

export default function ImageField({ value, onChange, label = "Bild", testId = "image-field" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const isNef = (file.name || "").toLowerCase().endsWith(".nef");
    if (!isNef && !file.type.startsWith("image/")) {
      toast.error("Filen måste vara en bild");
      return;
    }
    const limit = isNef ? 60 * 1024 * 1024 : 15 * 1024 * 1024;
    if (file.size > limit) {
      toast.error(`Bilden är för stor (max ${isNef ? 60 : 15} MB)`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success(isNef ? "NEF konverterad och uppladdad" : "Bild uppladdad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte ladda upp bilden");
    } finally {
      setUploading(false);
    }
  };

  const preview = resolveImageUrl(value);

  return (
    <div>
      {label && <div className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-2">{label}</div>}
      <div className="flex items-start gap-4">
        <div className="w-24 h-24 shrink-0 rounded-xl bg-[var(--surface-alt)] border border-[var(--border)] overflow-hidden grid place-items-center">
          {preview ? (
            <img src={preview} alt="förhandsvisning" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={24} className="text-[var(--text-muted)]" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,.nef,image/x-nikon-nef"
            ref={fileRef}
            className="hidden"
            data-testid={`${testId}-file`}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              data-testid={`${testId}-upload`}
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--text)] hover:opacity-90 text-white text-sm font-semibold px-4 py-2 disabled:opacity-60"
            >
              <Upload size={14} /> {uploading ? "Laddar upp…" : "Ladda upp"}
            </button>
            {value && (
              <button
                type="button"
                data-testid={`${testId}-remove`}
                onClick={() => onChange("")}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-white hover:border-red-400 hover:text-red-600 text-sm font-semibold px-4 py-2"
              >
                <ImageOff size={14} /> Ta bort
              </button>
            )}
          </div>
          <input
            type="text"
            placeholder="…eller klistra in bild-URL"
            data-testid={`${testId}-url`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--primary)]"
          />
        </div>
      </div>
    </div>
  );
}
